import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { platformClient } from "@homy/api-client";

const PlatformAuthContext = createContext(null);

const STORAGE_KEY = "homy_platform_session";

export function PlatformAuthProvider({ children }) {
  const [operator, setOperator] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize session
  useEffect(() => {
    async function initSession() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.token) {
            platformClient.defaults.headers.common["Authorization"] = `Bearer ${parsed.token}`;
          }
          if (parsed?.user) {
            setOperator(parsed.user);
          }
        }

        // Verify active session with backend /me
        const res = await platformClient.get("/auth/me");
        if (res.data) {
          setOperator(res.data);
          setError(null);
        }
      } catch (err) {
        // If 401/403 or network issue without saved session
        const stored = localStorage.getItem(STORAGE_KEY);
        if (err.response?.status === 401 || err.response?.status === 403) { localStorage.removeItem(STORAGE_KEY); setOperator(null); delete platformClient.defaults.headers.common["Authorization"]; } else if (!stored) {
          setOperator(null);
          delete platformClient.defaults.headers.common["Authorization"];
        }
      } finally {
        setIsLoading(false);
      }
    }

    initSession();
  }, []);

  const login = useCallback(async ({ email, password, token: ssoToken }) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await platformClient.post("/auth/exchange", {
        email,
        password: password || undefined,
        token: ssoToken || undefined,
      });

      const { token, user } = res.data;
      if (token) {
        platformClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }

      setOperator(user);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
      return user;
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Authentication failed. Please verify your platform administrator credentials.";
      setError(msg);
      const customErr = new Error(msg);
      customErr.status = err.response?.status;
      customErr.response = err.response;
      throw customErr;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setOperator(null);
    delete platformClient.defaults.headers.common["Authorization"];
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = {
    operator,
    isAuthenticated: Boolean(operator),
    isLoading,
    error,
    login,
    logout,
  };

  return (
    <PlatformAuthContext.Provider value={value}>
      {children}
    </PlatformAuthContext.Provider>
  );
}

export function usePlatformAuth() {
  const context = useContext(PlatformAuthContext);
  if (!context) {
    throw new Error("usePlatformAuth must be used within a PlatformAuthProvider");
  }
  return context;
}
